import { Button } from '@/components/ui/button'
import { Drawer, DrawerContentRight } from '@/components/ui/drawer'
import { SectionHeading } from '@/components/ui/section'
import { useConnectionRows } from '@/hooks/useConnectionRows'
import { useSettingsPanelState } from '@/hooks/useSettingsPanelState'
import { useConnections } from '@/queries/connections/useConnections'
import { useStytchOrganization } from '@stytch/react/b2b'
import { AlertCircle } from 'lucide-react'
import { LayoutGrid, Plus, Table2 } from 'lucide-react'

import { ConnectionRecordPanel } from './ConnectionRecordPanel'
import { ConnectionsCards } from './ConnectionsCards'
import { ConnectionsTable } from './ConnectionsTable'

import type { ConnectionRowPresentation } from './types'

export const ConnectionsList = () => {
	const { organization } = useStytchOrganization()
	const { data: apiConnections, isLoading, isError, error } = useConnections(organization?.organization_id)
	const { state, openRecord, openCreate, closePanel, setView } = useSettingsPanelState('connections')

	// Transform API connections into display rows
	const connectionRows = useConnectionRows(apiConnections)

	const selected = state.recordId ? connectionRows.find((row) => row.id === state.recordId) : undefined

	const handleSelect = (row: ConnectionRowPresentation) => {
		openRecord(row.id)
	}

	const handleAdd = () => {
		openCreate()
	}

	const handleDrawerChange = (open: boolean) => {
		if (!open) {
			closePanel()
		}
	}

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-center justify-between mb-4">
				<SectionHeading>Connections</SectionHeading>
				<div className="flex items-center gap-3">
					<div className="flex border rounded-md">
						<Button
							variant={state.view === 'table' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('table')}
							className="rounded-r-none border-r"
							aria-label="Table view"
						>
							<Table2 className="h-4 w-4" />
						</Button>
						<Button
							variant={state.view === 'cards' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('cards')}
							className="rounded-l-none"
							aria-label="Card view"
						>
							<LayoutGrid className="h-4 w-4" />
						</Button>
					</div>
					<Button onClick={handleAdd} size="sm">
						<Plus className="h-4 w-4 mr-2" />
						Add Connection
					</Button>
				</div>
			</div>

			<div className="flex-1 overflow-auto">
				{isError ? (
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<AlertCircle className="h-12 w-12 text-destructive mb-4" />
						<h3 className="text-lg font-semibold mb-2">Failed to load connections</h3>
						<p className="text-sm text-muted-foreground max-w-md">
							{error instanceof Error ? error.message : 'An unexpected error occurred'}
						</p>
					</div>
				) : isLoading ? (
					<div className="text-center py-8 text-muted-foreground">Loading connections...</div>
				) : state.view === 'table' ? (
					<ConnectionsTable
						connectionRows={connectionRows}
						onSelectConnection={handleSelect}
						selectedConnectionId={state.recordId ?? undefined}
					/>
				) : (
					<ConnectionsCards
						connectionRows={connectionRows}
						onSelectConnection={handleSelect}
						selectedConnectionId={state.recordId ?? undefined}
					/>
				)}
			</div>

			<Drawer open={state.isOpen} onOpenChange={handleDrawerChange} direction="right">
				<DrawerContentRight>
					<ConnectionRecordPanel onClose={closePanel} connectionRow={selected} isNew={state.mode === 'new'} />
				</DrawerContentRight>
			</Drawer>
		</div>
	)
}
